import { Model } from '@/models/Model';
import ErrorHandler from '@/support/ErrorHandler';
import { cloneDeep, debounce, isArray } from 'lodash';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

@Component<Autocomplete>({})
export default class Autocomplete extends Vue {
  //#region @Props
  /**
  * ie.
  * @Prop()
  * protected user!: User
  */

  @Prop()
  protected model!: Model;

  @Prop()
  protected filteredItems!: any[];

  @Prop()
  protected filterKey!: string;

  @Prop({ default: 'id' })
  protected itemValue!: string;

  @Prop({ default: 'name' })
  protected itemText!: string;
  //#endregion

  //#region @Refs
  /**
  * ie.
  * @Ref()
  * readonly anotherComponent!: AnotherComponent
  */
  //#endregion


  //#region Class properties
  /**
  * ie.
  * protected isLoading = true;
  * * protected company: Company | null = null;
  */

  protected internalValue: any = this.$attrs.value || null;
  protected items: Model[] = [];
  protected debouncedSearch: Function = debounce(this.getItems, 300);
  protected searchValue = '';
  //#endregion


  //#region Lifecycle Hooks / Init
  /**
  * ie.
  * protected mounted(): void {
  *  console.log('MyComponent has Mounted');
  * }
  */

  protected mounted() {
    if (this.$attrs['multiple'] !== undefined) {
      this.initializeMultiple();
    } else {
      this.initializeSingular();
    }
  }
  //#endregion


  //#region Class methods
  /**
  * ie.
  * protected toggleLoading(): void {
  *  this.isLoading = ! this.isLoading;
  * }
  */

  protected handleBlur() {
    this.searchValue = '';
    this.getItems();
  }

  protected async initializeSingular() {
     if (this.internalValue) {
      this.items.push(this.internalValue);
    } else {
      await this.getItems();
    }
  }

  protected async initializeMultiple() {
    if (this.internalValue && this.internalValue.length) {
      this.items = cloneDeep(this.internalValue);
    } else {
      await this.getItems();
    }
  }

  protected filterItems(items: Model[]) {
    this.items = items.filter(item => {
      if (this.filteredItems.some(filteredItem => {
        return (item as any)[this.filterKey] === filteredItem[this.filterKey];
      })) {
        return false;
      }

      return item;
    });
  }
  //#endregion


  //#region Async methods
  /**
  * ie.
  * protected async fetchUserCompany(): Promise<void> {
  *  this.company = await new Company().filter({user: this.user.id}).all();
  * }
  */

  protected addSelectedToItemsIfNotAlreadyInItems(items: any): any {
    if (isArray(items)) {
      if (isArray(this.internalValue)) {
        const itemIds = items.map((item: any) => item && typeof item === 'object' ? item[this.model.resolvePrimaryKey() || 'id'] : item);
        const internalValueExclAlreadyInItems = this.internalValue.filter((item: any) => !itemIds.includes(item && typeof item === 'object' ? item[this.model.resolvePrimaryKey() || 'id'] : item));
        items = [...internalValueExclAlreadyInItems, ...items];

      } else if (this.internalValue) {
        const itemIds = items.map((item: any) => item && typeof item === 'object' ? item[this.model.resolvePrimaryKey() || 'id'] : item);

        if (!itemIds.includes(this.internalValue && typeof this.internalValue === 'object' ? this.internalValue[this.model.resolvePrimaryKey() || 'id'] : this.internalValue)) {
          items = [this.internalValue, ...items];
        }
      }
    }

    return items;
  }

  protected async getItems() {
    try {
      let items = await this.model.autocomplete(this.searchValue);

      items = this.addSelectedToItemsIfNotAlreadyInItems(items);

      if (this.filteredItems && this.filterKey) {
        this.filterItems(items);
      } else {
        this.items = items;
      }
    } catch (error) {
      ErrorHandler.network(error);
    }
  }
  //#endregion


  //#region Getters & Setters
  /**
  * ie.
  * protected get companyName(): string {
  *  return this?.company.name || 'N/A';
  * }
  */
  //#endregion


  //#region @Watchers
  /**
  * ie.
  * @Watch('company')
  * protected companyChanged() {
  *  console.log('Company Changed') ;
  * }
  */

  @Watch('$attrs.value')
  public onChangedValue(newValue: unknown, oldValue: unknown): void {
    if (oldValue === newValue) return;
    this.internalValue = newValue;
  }

  @Watch('internalValue')
  public onChangedInternalValue(newValue: any, oldValue: any): void {
    if (oldValue === newValue) return;
    this.$emit('input', newValue);
  }
  //#endregion
}
